import { supabase } from './supabase';
import { startOfDay, endOfDay, addDays, startOfMonth, endOfMonth, setDate, differenceInDays, differenceInCalendarDays, format } from 'date-fns';

export interface PayrollPeriodSettings {
  id?: string;
  business_id: string;
  period_type: 'weekly' | 'biweekly' | 'semi_monthly' | 'monthly' | 'custom_semi_monthly';
  start_date?: string; // ISO date string (YYYY-MM-DD) - optional for custom_semi_monthly
  // Custom semi-monthly period configuration
  period_1_start_day?: number; // Day of month (1-31)
  period_1_end_day?: number; // Day of month (1-31)
  period_2_start_day?: number; // Day of month (1-31)
  period_2_end_day?: number; // Day of month (1-31)
  accountant_email?: string | null;
  // Overtime configuration (calculated server-side; entries on/after the
  // effective date get regular/overtime splits written by the database)
  overtime_enabled?: boolean;
  overtime_effective_from?: string | null; // ISO date (YYYY-MM-DD)
  work_week_start_day?: number; // 0 = Sunday ... 6 = Saturday
  daily_ot_threshold_hours?: number;
  daily_dt_threshold_hours?: number;
  weekly_ot_threshold_hours?: number;
  ot_multiplier?: number;
  dt_multiplier?: number;
  /** hours_minutes writes 8h 23m. decimal writes 8.38. */
  hours_format?: 'hours_minutes' | 'decimal';
  created_at?: string;
  updated_at?: string;
}

export interface DateRange {
  start: Date;
  end: Date;
}

export function formatPayrollPeriodDateKey(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/** Parse yyyy-MM-dd (or ISO) strings as local calendar dates for payroll queries. */
export function parsePayrollPeriodRange(
  startDate: string,
  endDate: string,
): DateRange {
  const parseLocalDate = (value: string) => {
    const datePart = value.includes('T') ? value.split('T')[0] : value;
    const [year, month, day] = datePart.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  return {
    start: startOfDay(parseLocalDate(startDate)),
    end: endOfDay(parseLocalDate(endDate)),
  };
}

export function formatPayrollPeriodType(
  periodType: PayrollPeriodSettings['period_type']
): string {
  switch (periodType) {
    case 'weekly':
      return 'Weekly';
    case 'biweekly':
      return 'Bi-weekly';
    case 'semi_monthly':
      return 'Semi-monthly';
    case 'custom_semi_monthly':
      return 'Custom semi-monthly';
    case 'monthly':
      return 'Monthly';
    default:
      return 'Payroll period';
  }
}

/** First calendar day payroll can be run (day after the period's last day). */
export function getPayrollPeriodDueDate(periodEnd: Date): Date {
  return startOfDay(addDays(periodEnd, 1));
}

/** True while the period is still open for hours/entries (includes its last calendar day). */
export function isPayrollPeriodStillOpen(
  periodEnd: Date,
  referenceDate: Date = new Date()
): boolean {
  return differenceInCalendarDays(startOfDay(referenceDate), startOfDay(periodEnd)) <= 0;
}

/** Days relative to the payroll due date (0 = due today, positive = overdue). */
export function getPayrollPeriodDaysPastDue(
  periodEnd: Date,
  referenceDate: Date = new Date()
): number {
  return differenceInCalendarDays(
    startOfDay(referenceDate),
    getPayrollPeriodDueDate(periodEnd)
  );
}

/** Label for when payroll should be processed (due the day after period end). */
export function getPayrollPeriodDueLabel(
  periodEnd: Date,
  referenceDate: Date = new Date()
): string {
  if (isPayrollPeriodStillOpen(periodEnd, referenceDate)) {
    const daysUntilClose = differenceInCalendarDays(
      startOfDay(periodEnd),
      startOfDay(referenceDate)
    );
    if (daysUntilClose === 0) return 'Period ends today';
    if (daysUntilClose === 1) return 'Period ends tomorrow';
    return `Period ends in ${daysUntilClose} days`;
  }

  const days = getPayrollPeriodDaysPastDue(periodEnd, referenceDate);
  if (days === 0) return 'Due today';
  if (days === 1) return '1 day past due';
  return `${days} days past due`;
}

/**
 * Get payroll period settings for a business
 */
export async function getPayrollPeriodSettings(businessId: string): Promise<PayrollPeriodSettings | null> {
  const { data, error } = await supabase
    .from('payroll_period_settings')
    .select('*')
    .eq('business_id', businessId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      // No settings found - this is ok
      return null;
    }
    console.error('Error fetching payroll period settings:', error);
    throw error;
  }

  return data;
}

/**
 * Save (upsert) payroll period settings for a business
 */
export async function savePayrollPeriodSettings(settings: PayrollPeriodSettings): Promise<PayrollPeriodSettings> {
  const { data, error } = await supabase
    .from('payroll_period_settings')
    .upsert({
      business_id: settings.business_id,
      period_type: settings.period_type,
      start_date: settings.start_date || null,
      period_1_start_day: settings.period_1_start_day || null,
      period_1_end_day: settings.period_1_end_day || null,
      period_2_start_day: settings.period_2_start_day || null,
      period_2_end_day: settings.period_2_end_day || null,
      accountant_email: settings.accountant_email?.trim() || null,
      overtime_enabled: settings.overtime_enabled ?? false,
      overtime_effective_from: settings.overtime_effective_from || null,
      work_week_start_day: settings.work_week_start_day ?? 0,
      daily_ot_threshold_hours: settings.daily_ot_threshold_hours ?? 8,
      daily_dt_threshold_hours: settings.daily_dt_threshold_hours ?? 12,
      weekly_ot_threshold_hours: settings.weekly_ot_threshold_hours ?? 40,
      ot_multiplier: settings.ot_multiplier ?? 1.5,
      dt_multiplier: settings.dt_multiplier ?? 2,
      hours_format: settings.hours_format === 'decimal' ? 'decimal' : 'hours_minutes',
      updated_at: new Date().toISOString()
    }, {
      onConflict: 'business_id'
    })
    .select()
    .single();

  if (error) {
    console.error('Error saving payroll period settings:', error);
    throw error;
  }

  return data;
}

/**
 * Save only the hours label. Flipping the switch should not wait on the pay-period form,
 * and it should not create a pay period for a business that has never saved one.
 */
export async function savePayrollHoursFormat(
  businessId: string,
  hoursFormat: 'hours_minutes' | 'decimal',
): Promise<PayrollPeriodSettings | null> {
  const { data, error } = await supabase
    .from('payroll_period_settings')
    .update({
      hours_format: hoursFormat,
      updated_at: new Date().toISOString(),
    })
    .eq('business_id', businessId)
    .select()
    .maybeSingle();

  if (error) {
    console.error('Error saving payroll hours format:', error);
    throw error;
  }

  if (data) return data;

  const { error: appError } = await supabase.rpc('set_app_setting', {
    p_business_id: businessId,
    p_app_slug: 'payroll',
    p_setting_key: 'decimal_hours',
    p_setting_value: hoursFormat === 'decimal',
  });

  if (appError) {
    console.error('Error saving payroll hours format:', appError);
    throw appError;
  }

  return null;
}

/** The switch state: the pay-period row when one exists, otherwise the live app setting. */
export async function loadPayrollHoursFormat(
  businessId: string,
  settings: PayrollPeriodSettings | null,
): Promise<'hours_minutes' | 'decimal'> {
  if (settings) return settings.hours_format === 'decimal' ? 'decimal' : 'hours_minutes';

  const { data, error } = await supabase
    .from('app_settings')
    .select('setting_value')
    .eq('business_id', businessId)
    .eq('app_slug', 'payroll')
    .eq('setting_key', 'decimal_hours')
    .maybeSingle();

  if (error) {
    console.error('Error loading payroll hours format:', error);
    return 'hours_minutes';
  }

  return data?.setting_value === true ? 'decimal' : 'hours_minutes';
}

/**
 * Recalculate overtime for all past UNPAID timeclock entries.
 * Runs server-side; PAID entries are never modified. Returns weeks processed.
 */
export async function applyOvertimeToUnpaidHistory(businessId: string): Promise<number> {
  const { data, error } = await supabase.rpc('apply_overtime_to_unpaid_history', {
    p_business_id: businessId,
  });

  if (error) {
    console.error('Error applying overtime to unpaid history:', error);
    throw error;
  }

  return data ?? 0;
}

/**
 * Delete payroll period settings for a business
 */
export async function deletePayrollPeriodSettings(businessId: string): Promise<void> {
  const { error } = await supabase
    .from('payroll_period_settings')
    .delete()
    .eq('business_id', businessId);

  if (error) {
    console.error('Error deleting payroll period settings:', error);
    throw error;
  }
}

/**
 * Calculate the current payroll period based on settings
 */
export function getCurrentPeriodRange(settings: PayrollPeriodSettings): DateRange {
  return getPeriodRangeContainingDate(settings, new Date());
}

/**
 * Payroll period that contains the given date (same rules as current period).
 */
export function getPeriodRangeContainingDate(
  settings: PayrollPeriodSettings,
  referenceDate: Date
): DateRange {
  const today = referenceDate;
  
  // Parse date as local date to avoid timezone issues (only for types that use start_date)
  let startDate: Date | undefined;
  if (settings.start_date) {
    const [year, month, day] = settings.start_date.split('-').map(Number);
    startDate = new Date(year, month - 1, day);
  }

  switch (settings.period_type) {
    case 'weekly': {
      // Find which week we're in since the start date
      // Use startOfDay to ensure we're comparing dates, not datetime
      const daysSinceStart = differenceInDays(startOfDay(today), startOfDay(startDate!));
      const weekNumber = Math.floor(daysSinceStart / 7);
      const periodStart = addDays(startDate!, weekNumber * 7);
      const periodEnd = addDays(periodStart, 6);
      
      return {
        start: startOfDay(periodStart),
        end: endOfDay(periodEnd)
      };
    }

    case 'biweekly': {
      // Find which 2-week period we're in since the start date
      // Use startOfDay to ensure we're comparing dates, not datetime
      const daysSinceStart = differenceInDays(startOfDay(today), startOfDay(startDate!));
      const periodNumber = Math.floor(daysSinceStart / 14);
      const periodStart = addDays(startDate!, periodNumber * 14);
      const periodEnd = addDays(periodStart, 13);
      
      return {
        start: startOfDay(periodStart),
        end: endOfDay(periodEnd)
      };
    }

    case 'semi_monthly': {
      // First half: 1st-15th, Second half: 16th-end of month
      const currentDay = today.getDate();
      
      if (currentDay <= 15) {
        return {
          start: startOfDay(setDate(today, 1)),
          end: endOfDay(setDate(today, 15))
        };
      } else {
        return {
          start: startOfDay(setDate(today, 16)),
          end: endOfDay(endOfMonth(today))
        };
      }
    }

    case 'custom_semi_monthly': {
      // Custom periods defined by day numbers (can cross month boundaries)
      const currentDay = today.getDate();
      const p1Start = settings.period_1_start_day!;
      const p1End = settings.period_1_end_day!;
      const p2Start = settings.period_2_start_day!;
      const p2End = settings.period_2_end_day!;

      // Helper function to check if current day is within a period
      const isInPeriod = (start: number, end: number, day: number): boolean => {
        if (start <= end) {
          // Period within same month (e.g., 11-25)
          return day >= start && day <= end;
        } else {
          // Period crosses month boundary (e.g., 26-10)
          return day >= start || day <= end;
        }
      };

      // Determine which period we're in
      if (isInPeriod(p1Start, p1End, currentDay)) {
        // We're in period 1
        if (p1Start <= p1End) {
          // Period 1 is within same month
          return {
            start: startOfDay(setDate(today, p1Start)),
            end: endOfDay(setDate(today, p1End))
          };
        } else {
          // Period 1 crosses month boundary (e.g., 26th-10th)
          if (currentDay >= p1Start) {
            // We're in the first part (e.g., 26th-31st)
            return {
              start: startOfDay(setDate(today, p1Start)),
              end: endOfDay(setDate(addDays(today, 32 - currentDay + p1End), p1End))
            };
          } else {
            // We're in the second part (e.g., 1st-10th)
            const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, p1Start);
            return {
              start: startOfDay(lastMonth),
              end: endOfDay(setDate(today, p1End))
            };
          }
        }
      } else {
        // We're in period 2
        if (p2Start <= p2End) {
          // Period 2 is within same month
          return {
            start: startOfDay(setDate(today, p2Start)),
            end: endOfDay(setDate(today, p2End))
          };
        } else {
          // Period 2 crosses month boundary
          if (currentDay >= p2Start) {
            // We're in the first part
            return {
              start: startOfDay(setDate(today, p2Start)),
              end: endOfDay(setDate(addDays(today, 32 - currentDay + p2End), p2End))
            };
          } else {
            // We're in the second part
            const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, p2Start);
            return {
              start: startOfDay(lastMonth),
              end: endOfDay(setDate(today, p2End))
            };
          }
        }
      }
    }

    case 'monthly': {
      // Period starts on the same day of month as start_date
      const startDay = startDate!.getDate();
      const currentDay = today.getDate();
      
      let periodStart: Date;
      
      if (currentDay >= startDay) {
        // We're in the current month's period
        periodStart = setDate(today, startDay);
      } else {
        // We're in the period that started last month
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, startDay);
        periodStart = lastMonth;
      }
      
      // Period ends the day before next period starts
      const nextPeriodStart = new Date(periodStart);
      nextPeriodStart.setMonth(nextPeriodStart.getMonth() + 1);
      const periodEnd = addDays(nextPeriodStart, -1);
      
      return {
        start: startOfDay(periodStart),
        end: endOfDay(periodEnd)
      };
    }

    default:
      // Fallback to current month
      return {
        start: startOfMonth(today),
        end: endOfMonth(today)
      };
  }
}

/**
 * Period immediately before the given period.
 */
export function getPreviousPeriodRange(
  settings: PayrollPeriodSettings,
  period: DateRange
): DateRange {
  return getPeriodRangeContainingDate(settings, addDays(period.start, -1));
}

/**
 * Ended payroll periods before today, most recent first.
 */
export function getPastEndedPeriodRanges(
  settings: PayrollPeriodSettings,
  maxCount = 24
): DateRange[] {
  const today = startOfDay(new Date());
  const periods: DateRange[] = [];
  let cursor = getLastPeriodRange(settings);

  while (startOfDay(cursor.end) >= today) {
    const previous = getPreviousPeriodRange(settings, cursor);
    if (previous.start.getTime() === cursor.start.getTime()) break;
    cursor = previous;
  }

  while (periods.length < maxCount) {
    periods.push(cursor);

    const previous = getPreviousPeriodRange(settings, cursor);
    if (previous.start.getTime() === cursor.start.getTime()) break;

    if (settings.start_date) {
      const [year, month, day] = settings.start_date.split('-').map(Number);
      const settingsStart = startOfDay(new Date(year, month - 1, day));
      if (startOfDay(previous.end) < settingsStart) break;
    }

    cursor = previous;
  }

  return periods;
}

/**
 * Calculate the last payroll period based on settings
 */
export function getLastPeriodRange(settings: PayrollPeriodSettings): DateRange {
  const currentPeriod = getCurrentPeriodRange(settings);
  
  // Parse date as local date to avoid timezone issues (only for types that use start_date)
  let startDate: Date | undefined;
  if (settings.start_date) {
    const [year, month, day] = settings.start_date.split('-').map(Number);
    startDate = new Date(year, month - 1, day);
  }

  switch (settings.period_type) {
    case 'weekly': {
      // Last period is 7 days before current period
      const periodStart = addDays(currentPeriod.start, -7);
      const periodEnd = addDays(currentPeriod.start, -1);
      
      return {
        start: startOfDay(periodStart),
        end: endOfDay(periodEnd)
      };
    }

    case 'biweekly': {
      // Last period is 14 days before current period
      const periodStart = addDays(currentPeriod.start, -14);
      const periodEnd = addDays(currentPeriod.start, -1);
      
      return {
        start: startOfDay(periodStart),
        end: endOfDay(periodEnd)
      };
    }

    case 'semi_monthly': {
      const currentDay = new Date().getDate();
      const today = new Date();
      
      if (currentDay <= 15) {
        // We're in first half, so last period is second half of previous month
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        return {
          start: startOfDay(setDate(lastMonth, 16)),
          end: endOfDay(endOfMonth(lastMonth))
        };
      } else {
        // We're in second half, so last period is first half of current month
        return {
          start: startOfDay(setDate(today, 1)),
          end: endOfDay(setDate(today, 15))
        };
      }
    }

    case 'custom_semi_monthly': {
      // Simply go back from the current period start to find the last period
      const p1Start = settings.period_1_start_day!;
      const p1End = settings.period_1_end_day!;
      const p2Start = settings.period_2_start_day!;
      const p2End = settings.period_2_end_day!;

      // Calculate how many days the current period is
      let currentPeriodDays: number;
      if (currentPeriod.start.getDate() === p1Start) {
        // Current is period 1, calculate its length
        currentPeriodDays = p1Start <= p1End 
          ? (p1End - p1Start + 1)
          : (31 - p1Start + 1 + p1End); // crosses month boundary
      } else {
        // Current is period 2, calculate its length
        currentPeriodDays = p2Start <= p2End 
          ? (p2End - p2Start + 1)
          : (31 - p2Start + 1 + p2End); // crosses month boundary
      }

      // Go back from current period start
      const lastPeriodEnd = addDays(currentPeriod.start, -1);
      
      // Determine which period the last one was and calculate its start
      const lastEndDay = lastPeriodEnd.getDate();
      let lastPeriodStart: Date;

      // Check if last period ended on p1End or p2End
      if (lastEndDay === p1End || (p1Start > p1End && lastEndDay === p1End)) {
        // Last period was period 1
        if (p1Start <= p1End) {
          // Period 1 within same month
          lastPeriodStart = setDate(lastPeriodEnd, p1Start);
        } else {
          // Period 1 crosses month boundary
          const prevMonth = new Date(lastPeriodEnd.getFullYear(), lastPeriodEnd.getMonth() - 1, p1Start);
          lastPeriodStart = prevMonth;
        }
      } else {
        // Last period was period 2
        if (p2Start <= p2End) {
          // Period 2 within same month
          lastPeriodStart = setDate(lastPeriodEnd, p2Start);
        } else {
          // Period 2 crosses month boundary
          const prevMonth = new Date(lastPeriodEnd.getFullYear(), lastPeriodEnd.getMonth() - 1, p2Start);
          lastPeriodStart = prevMonth;
        }
      }

      return {
        start: startOfDay(lastPeriodStart),
        end: endOfDay(lastPeriodEnd)
      };
    }

    case 'monthly': {
      // Last period is one month before current period
      const periodStart = addDays(currentPeriod.start, -1);
      const startDay = startDate!.getDate();
      
      // Go back to find the start of last period
      let lastPeriodStart = new Date(periodStart);
      lastPeriodStart.setDate(startDay);
      
      // If we went forward in time, go back one more month
      if (lastPeriodStart > periodStart) {
        lastPeriodStart.setMonth(lastPeriodStart.getMonth() - 1);
      }
      
      return {
        start: startOfDay(lastPeriodStart),
        end: endOfDay(addDays(currentPeriod.start, -1))
      };
    }

    default:
      // Fallback to last month
      const today = new Date();
      return {
        start: startOfMonth(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        end: endOfMonth(new Date(today.getFullYear(), today.getMonth() - 1, 1))
      };
  }
}

/**
 * Get preview of upcoming pay periods (for settings UI)
 */
export function getUpcomingPeriods(settings: PayrollPeriodSettings, count: number = 3): DateRange[] {
  const periods: DateRange[] = [];
  const currentPeriod = getCurrentPeriodRange(settings);
  
  // Start with current period
  periods.push(currentPeriod);
  
  // Calculate future periods
  for (let i = 1; i < count; i++) {
    const lastPeriod = periods[periods.length - 1];
    
    switch (settings.period_type) {
      case 'weekly': {
        const nextStart = addDays(lastPeriod.end, 1);
        const nextEnd = addDays(nextStart, 6);
        periods.push({
          start: startOfDay(nextStart),
          end: endOfDay(nextEnd)
        });
        break;
      }
      
      case 'biweekly': {
        const nextStart = addDays(lastPeriod.end, 1);
        const nextEnd = addDays(nextStart, 13);
        periods.push({
          start: startOfDay(nextStart),
          end: endOfDay(nextEnd)
        });
        break;
      }
      
      case 'semi_monthly': {
        const lastEnd = lastPeriod.end;
        const lastDay = lastEnd.getDate();
        
        if (lastDay === 15) {
          // Next period is 16th to end of month
          periods.push({
            start: startOfDay(setDate(lastEnd, 16)),
            end: endOfDay(endOfMonth(lastEnd))
          });
        } else {
          // Next period is 1st-15th of next month
          const nextMonth = new Date(lastEnd.getFullYear(), lastEnd.getMonth() + 1, 1);
          periods.push({
            start: startOfDay(nextMonth),
            end: endOfDay(setDate(nextMonth, 15))
          });
        }
        break;
      }

      case 'custom_semi_monthly': {
        // Alternate between period 1 and period 2
        const p1Start = settings.period_1_start_day!;
        const p1End = settings.period_1_end_day!;
        const p2Start = settings.period_2_start_day!;
        const p2End = settings.period_2_end_day!;
        
        // Determine which period the last one was
        const lastEndDay = lastPeriod.end.getDate();
        const wasInPeriod1 = (p1Start <= p1End) 
          ? (lastEndDay === p1End)
          : (lastEndDay === p1End || (p1Start > p1End && lastEndDay >= p1Start));
        
        if (wasInPeriod1) {
          // Next is period 2
          if (p2Start <= p2End) {
            // Period 2 is within same month
            const nextStart = addDays(lastPeriod.end, 1);
            periods.push({
              start: startOfDay(setDate(nextStart, p2Start)),
              end: endOfDay(setDate(nextStart, p2End))
            });
          } else {
            // Period 2 crosses month boundary
            const nextStart = addDays(lastPeriod.end, 1);
            const nextMonth = new Date(nextStart.getFullYear(), nextStart.getMonth() + 1, p2End);
            periods.push({
              start: startOfDay(setDate(nextStart, p2Start)),
              end: endOfDay(nextMonth)
            });
          }
        } else {
          // Next is period 1
          if (p1Start <= p1End) {
            // Period 1 is within same month
            const nextStart = addDays(lastPeriod.end, 1);
            periods.push({
              start: startOfDay(setDate(nextStart, p1Start)),
              end: endOfDay(setDate(nextStart, p1End))
            });
          } else {
            // Period 1 crosses month boundary
            const nextStart = addDays(lastPeriod.end, 1);
            const nextMonth = new Date(nextStart.getFullYear(), nextStart.getMonth() + 1, p1End);
            periods.push({
              start: startOfDay(setDate(nextStart, p1Start)),
              end: endOfDay(nextMonth)
            });
          }
        }
        break;
      }
      
      case 'monthly': {
        const [year, month, day] = settings.start_date!.split('-').map(Number);
        const startDate = new Date(year, month - 1, day);
        const startDay = startDate.getDate();
        const nextStart = addDays(lastPeriod.end, 1);
        
        // Set to the start day of the next period
        let nextPeriodStart = setDate(nextStart, startDay);
        
        // If we went backwards, move forward a month
        if (nextPeriodStart < nextStart) {
          nextPeriodStart = new Date(nextPeriodStart);
          nextPeriodStart.setMonth(nextPeriodStart.getMonth() + 1);
        }
        
        // Period ends the day before next period starts
        const periodAfter = new Date(nextPeriodStart);
        periodAfter.setMonth(periodAfter.getMonth() + 1);
        const nextEnd = addDays(periodAfter, -1);
        
        periods.push({
          start: startOfDay(nextPeriodStart),
          end: endOfDay(nextEnd)
        });
        break;
      }
    }
  }
  
  return periods;
}

